/**
 * FrostedWaterPurifierModal — Apple Home 風格毛玻璃 淨水器 控制面板
 *
 * Indigo/Deep Blue tint — TDS dial + multi-stage filter cards
 * Mode: Auto / Pure / Hot / Cold
 * Flow Rate: Eco / Normal / Max
 *
 * 完全自包含：parent 只需傳 deviceSN + credentials
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import { executeCommandWithAuth, configureDeviceNicknameWithAuth } from '../services/turingMcpApi'
import './FrostedWaterPurifierModal.css'

export const WATER_PURIFIER_FROSTED_SECTIONS: ModalSectionMeta[] = [
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [] },
  { id: 'dial', label: 'TDS Dial', icon: 'water_drop', slots: [
    { id: 'dial-arc', label: 'Arc Stroke', editableProps: ['color', 'glow'] },
    { id: 'dial-tds', label: 'TDS Text', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'dial-label', label: 'TDS Label', editableProps: ['fontSize', 'color'] },
  ]},
  { id: 'mode', label: 'Mode', icon: 'auto_mode', slots: [
    { id: 'mode-bar', label: 'Mode Bar Container', editableProps: ['background', 'borderRadius', 'border'] },
    { id: 'mode-btn', label: 'Mode Button', editableProps: ['background', 'color', 'borderRadius', 'fontSize'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  { id: 'flow', label: 'Flow Rate', icon: 'speed', slots: [
    { id: 'flow-label', label: 'Section Label', editableProps: ['fontSize', 'color', 'letterSpacing'] },
    { id: 'flow-btn', label: 'Rate Button', editableProps: ['background', 'color', 'borderRadius', 'border'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  { id: 'filters', label: 'Filter Status', icon: 'filter_alt', slots: [] },
  { id: 'chart', label: 'TDS History', icon: 'show_chart', slots: [] },
]

const DEFAULT_SECTION_ORDER = WATER_PURIFIER_FROSTED_SECTIONS.map((s) => s.id)

const MODES = [
  { id: 'auto', label: 'Auto', icon: 'auto_mode' },
  { id: 'pure', label: 'Pure', icon: 'water_drop' },
  { id: 'hot', label: 'Hot', icon: 'local_cafe' },
  { id: 'cold', label: 'Cold', icon: 'ac_unit' },
] as const

const FLOW_RATES = [
  { id: 'eco', label: 'Eco' },
  { id: 'normal', label: 'Normal' },
  { id: 'max', label: 'Max' },
] as const

const TDS_MAX_RING = 500

function tdsLevel(tds: number): { label: string; color: string } {
  if (tds <= 50) return { label: 'TDS 純淨', color: 'rgba(74,222,128,0.85)' }
  if (tds <= 150) return { label: 'TDS 良好', color: 'rgba(250,204,21,0.85)' }
  if (tds <= 300) return { label: 'TDS 偏高', color: 'rgba(251,146,60,0.85)' }
  return { label: 'TDS 危險', color: 'rgba(239,68,68,0.85)' }
}

// Filter stage definitions
const FILTER_STAGES = [
  { id: 'pp', name: 'PP Sediment', icon: 'filter_1', colorClass: 'pp' },
  { id: 'carbon', name: 'Carbon Block', icon: 'filter_2', colorClass: 'carbon' },
  { id: 'ro', name: 'RO Membrane', icon: 'filter_3', colorClass: 'ro' },
] as const

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) { if (key in state && state[key] !== undefined && state[key] !== null) return state[key] }
  return undefined
}
function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  let v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  if (Array.isArray(v)) v = v[0]
  if (v === undefined || v === null) return null
  const n = Number(v); return isNaN(n) ? null : n
}
function strAttr(state: Record<string, unknown> | null, ...keys: string[]): string {
  const v = getAttr(state, ...keys); return v != null ? String(v) : ''
}

const RING_R = 85, RING_CX = 100, RING_CY = 100
const RING_CIRC = 2 * Math.PI * RING_R
const RING_ARC_RATIO = 0.75
const RING_TRACK_OFFSET = RING_CIRC * (1 - RING_ARC_RATIO)

function tdsToDashOffset(tds: number): number {
  const ratio = Math.max(0, Math.min(1, tds / TDS_MAX_RING))
  return RING_CIRC - ratio * RING_CIRC * RING_ARC_RATIO
}
const DEG = Math.PI / 180
function tdsToDotPos(tds: number): { x: number; y: number } {
  const ratio = Math.max(0, Math.min(1, tds / TDS_MAX_RING))
  const rad = (135 + ratio * 270) * DEG
  return { x: RING_CX + RING_R * Math.cos(rad), y: RING_CY + RING_R * Math.sin(rad) }
}

export function FrostedWaterPurifierModal({
  device, deviceState, onCommand, onClose, isEditorMode, cssOverrides,
  sectionOrder, sectionVisible, onSectionContextMenu, highlightedSection, credentials,
}: DeviceModalTemplateProps) {

  const handleContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!isEditorMode || !onSectionContextMenu) return
    const sectionEl = (event.target as HTMLElement)?.closest('[data-modal-section]') as HTMLElement | null
    if (!sectionEl) return
    event.preventDefault(); event.stopPropagation()
    const sectionId = sectionEl.dataset.modalSection || ''
    if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
  }, [isEditorMode, onSectionContextMenu])

  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    return [...order, ...DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))]
  }, [sectionOrder])
  const isSectionVisible = useCallback((id: string) => sectionVisible?.[id] !== false, [sectionVisible])

  const isOn = Boolean(getAttr(deviceState, 'on'))
  const tds = numAttr(deviceState, 'tds', 'TDS', 'totalDissolvedSolids') ?? 0
  const currentMode = strAttr(deviceState, 'currentModeSettings', 'mode') || 'auto'
  const currentFlowRate = strAttr(deviceState, 'flowRate', 'currentFlowRate') || 'normal'
  const filterPP = numAttr(deviceState, 'filterLifePP', 'ppFilterLife') ?? null
  const filterCarbon = numAttr(deviceState, 'filterLifeCarbon', 'carbonFilterLife') ?? null
  const filterRO = numAttr(deviceState, 'filterLifeRO', 'roFilterLife') ?? null
  const filterLives = [filterPP, filterCarbon, filterRO]

  const [showSettings, setShowSettings] = useState(false)
  const deviceInfoItems = useMemo(() => {
    const items: Array<{ label: string; value: string; action?: 'copy' }> = []
    items.push({ label: 'DEVICE TYPE', value: String(device.type || 'Unknown') })
    if (device.ip) items.push({ label: 'IP ADDRESS', value: String(device.ip) })
    if (device.fwVersion || device.firmwareVersion) items.push({ label: 'FW VERSION', value: String(device.fwVersion || device.firmwareVersion) })
    if (device.rssi != null) items.push({ label: 'RSSI', value: String(device.rssi) })
    items.push({ label: 'DEVICE SN', value: device.sn, action: 'copy' })
    return items
  }, [device])
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text).catch(() => {}); setCopiedField(label); setTimeout(() => setCopiedField(null), 1000)
  }, [])

  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [displayName, setDisplayName] = useState(device.name || device.sn)
  const renameInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => { setDisplayName(device.name || device.sn) }, [device.name, device.sn])
  const startRename = useCallback(() => { setRenameValue(displayName); setIsRenaming(true); setTimeout(() => renameInputRef.current?.focus(), 50) }, [displayName])
  const submitRename = useCallback(async () => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === displayName || !credentials) { setIsRenaming(false); return }
    setDisplayName(trimmed); setIsRenaming(false)
    await configureDeviceNicknameWithAuth(credentials, device.sn, trimmed).catch(() => {})
  }, [renameValue, displayName, credentials, device.sn])

  const [pendingMode, setPendingMode] = useState<string | null>(null)
  const [pendingFlow, setPendingFlow] = useState<string | null>(null)
  const [pendingPower, setPendingPower] = useState<boolean | null>(null)
  const displayMode = pendingMode ?? currentMode
  const displayFlow = pendingFlow ?? currentFlowRate
  const displayPower = pendingPower ?? isOn

  useEffect(() => { if (pendingMode !== null && pendingMode === currentMode) setPendingMode(null) }, [currentMode, pendingMode])
  useEffect(() => { if (pendingFlow !== null && pendingFlow === currentFlowRate) setPendingFlow(null) }, [currentFlowRate, pendingFlow])
  useEffect(() => { if (pendingPower !== null && pendingPower === isOn) setPendingPower(null) }, [isOn, pendingPower])

  const execCmd = useCallback((cmd: string, params?: Record<string, unknown>) => {
    if (!credentials || !device.sn) return
    executeCommandWithAuth(credentials, [device.sn], cmd, params).catch(() => {})
    if (onCommand) onCommand(cmd, params)
  }, [credentials, device.sn, onCommand])

  const togglePower = useCallback(() => {
    if (isEditorMode) return; const next = !displayPower; setPendingPower(next); execCmd(next ? 'on' : 'off')
  }, [isEditorMode, displayPower, execCmd])
  const setModeCmd = useCallback((mode: string) => { if (isEditorMode) return; setPendingMode(mode); execCmd('setMode', { mode }) }, [isEditorMode, execCmd])
  const setFlowCmd = useCallback((rate: string) => { if (isEditorMode) return; setPendingFlow(rate); execCmd('setFlowRate', { flowRate: rate }) }, [isEditorMode, execCmd])

  const displayTDS = displayPower ? tds : 0
  const valueDashOffset = tdsToDashOffset(displayTDS)
  const dotPos = tdsToDotPos(displayTDS)
  const tdsInfo = tdsLevel(tds)

  const stateClass = displayPower ? 'wp-frosted--on' : 'wp-frosted--off'
  const hl = (id: string) => highlightedSection === id ? ' section-highlighted' : ''

  const renderSection = (sectionId: string) => {
    if (!isSectionVisible(sectionId)) return null
    switch (sectionId) {
      case 'dial':
        return (
          <div key="dial" className={`wp-frosted__dial${hl('dial')}`} data-modal-section="dial">
            <svg viewBox="0 0 200 200" className="wp-frosted__ring-svg">
              <defs>
                <linearGradient id="wpIndigoGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#6366f1"/><stop offset="100%" stopColor="#818cf8"/>
                </linearGradient>
              </defs>
              <circle cx={RING_CX} cy={RING_CY} r={RING_R} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={16} strokeLinecap="round" strokeDasharray={RING_CIRC} strokeDashoffset={RING_TRACK_OFFSET} transform={`rotate(135,${RING_CX},${RING_CY})`}/>
              <circle cx={RING_CX} cy={RING_CY} r={RING_R} fill="none" stroke="url(#wpIndigoGrad)" strokeWidth={16} strokeLinecap="round" strokeDasharray={RING_CIRC} strokeDashoffset={displayPower ? valueDashOffset : RING_CIRC} transform={`rotate(135,${RING_CX},${RING_CY})`} style={{ transition: 'stroke-dashoffset 0.3s ease' }}/>
              {displayPower && <circle cx={dotPos.x} cy={dotPos.y} r={7} fill="#fff" style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))' }}/>}
            </svg>
            <div className="wp-frosted__dial-text">
              {displayPower ? (
                <>
                  <div><span className="wp-frosted__tds-value">{tds}</span><span className="wp-frosted__tds-unit">ppm</span></div>
                  <div className="wp-frosted__tds-label" style={{ color: tdsInfo.color }}>{tdsInfo.label}</div>
                </>
              ) : <span className="wp-frosted__off-text">OFF</span>}
            </div>
            <button className="wp-frosted__power-tap" onClick={togglePower} aria-label={displayPower ? 'Turn off' : 'Turn on'}/>
          </div>
        )
      case 'mode':
        return (
          <div key="mode" className={`wp-frosted__modes${hl('mode')}`} data-modal-section="mode">
            {MODES.map(m => (
              <button key={m.id} className={`wp-frosted__mode-btn${displayMode === m.id ? ' active' : ''}`} onClick={() => setModeCmd(m.id)}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{m.icon}</span> {m.label}
              </button>
            ))}
          </div>
        )
      case 'flow':
        return (
          <div key="flow" className={`wp-frosted__flow${hl('flow')}`} data-modal-section="flow">
            <div className="wp-frosted__flow-label">Flow Rate</div>
            <div className="wp-frosted__flow-buttons">
              {FLOW_RATES.map(f => (
                <button key={f.id} className={`wp-frosted__flow-btn${displayFlow === f.id ? ' active' : ''}`} onClick={() => setFlowCmd(f.id)}>{f.label}</button>
              ))}
            </div>
          </div>
        )
      case 'filters':
        return (
          <div key="filters" className={`wp-frosted__filter-cards${hl('filters')}`} data-modal-section="filters">
            {FILTER_STAGES.map((stage, i) => (
              <div key={stage.id} className="wp-frosted__filter-card">
                <div className={`wp-frosted__filter-icon wp-frosted__filter-icon--${stage.colorClass}`}>
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{stage.icon}</span>
                </div>
                <div className="wp-frosted__filter-info">
                  <div className="wp-frosted__filter-name">{stage.name}</div>
                  <div className="wp-frosted__filter-bar-wrap">
                    <div className="wp-frosted__filter-bar">
                      <div className={`wp-frosted__filter-bar-fill wp-frosted__filter-bar-fill--${stage.colorClass}`} style={{ width: `${filterLives[i] ?? 0}%` }}/>
                    </div>
                  </div>
                </div>
                <div className="wp-frosted__filter-pct">{filterLives[i] ?? '--'}%</div>
              </div>
            ))}
          </div>
        )
      case 'chart':
        return (
          <div key="chart" className={`wp-frosted__chart-section${hl('chart')}`} data-modal-section="chart">
            <div className="wp-frosted__chart-controls">
              <span className="wp-frosted__chart-ctrl"><span className="material-symbols-outlined" style={{ fontSize: 18 }}>schedule</span> 7D</span>
              <span className="wp-frosted__chart-ctrl"><span className="material-symbols-outlined" style={{ fontSize: 18 }}>science</span> TDS</span>
            </div>
            <div className="wp-frosted__chart-area">
              <svg viewBox="0 0 340 60" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
                <defs>
                  <linearGradient id="wpChartFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(99,102,241,0.3)"/><stop offset="100%" stopColor="rgba(99,102,241,0.02)"/>
                  </linearGradient>
                </defs>
                <path d="M0,30 C30,28 60,25 90,22 C120,20 150,18 180,20 C210,22 240,25 270,22 C300,18 320,16 340,18" fill="none" stroke="rgba(99,102,241,0.7)" strokeWidth="2"/>
                <path d="M0,30 C30,28 60,25 90,22 C120,20 150,18 180,20 C210,22 240,25 270,22 C300,18 320,16 340,18 L340,60 L0,60 Z" fill="url(#wpChartFill)"/>
              </svg>
            </div>
            <div className="wp-frosted__chart-stats"><span>Now {tds || '--'}</span><span>Max --</span><span>Min --</span></div>
          </div>
        )
      default: return null
    }
  }

  return (
    <div className={`wp-frosted ${stateClass}`} data-modal-template-id="water-purifier-frosted" onContextMenu={handleContextMenu}>
      {cssOverrides && <style>{cssOverrides}</style>}
      <div className="wp-frosted__header" data-modal-section="_modal">
        <button className="wp-frosted__back" onClick={onClose} aria-label="Back">‹</button>
        <span className="wp-frosted__name">{displayName}</span>
        <button className={`wp-frosted__power-btn${displayPower ? ' wp-frosted__power-btn--on' : ''}`} onClick={togglePower} aria-label="Power">
          <span className="material-symbols-outlined" style={{ fontSize: 20 }}>power_settings_new</span>
        </button>
      </div>
      {orderedSections.map(id => renderSection(id))}
      <button className="wp-frosted__settings-btn" onClick={() => setShowSettings(true)} aria-label="Settings">
        <span className="material-symbols-outlined" style={{ fontSize: 28 }}>settings</span>
      </button>
      {showSettings && (
        <div className="wp-frosted__settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="wp-frosted__settings-sheet" onClick={e => e.stopPropagation()}>
            <div className="wp-frosted__settings-header">
              <span className="wp-frosted__settings-title">設定</span>
              <button className="wp-frosted__settings-close" onClick={() => setShowSettings(false)}>✕</button>
            </div>
            <div className="wp-frosted__settings-section">
              <div className="wp-frosted__settings-section-title">裝置名稱</div>
              {isRenaming ? (
                <div className="wp-frosted__settings-row">
                  <input ref={renameInputRef} className="wp-frosted__rename-input" value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setIsRenaming(false) }}
                    onBlur={submitRename} maxLength={40}/>
                </div>
              ) : (
                <div className="wp-frosted__settings-row">
                  <span className="wp-frosted__settings-value" style={{ textAlign: 'left', flex: 1 }}>{displayName}</span>
                  <button className="wp-frosted__settings-edit" onClick={startRename} title="Rename">
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                  </button>
                </div>
              )}
            </div>
            <div className="wp-frosted__settings-section">
              <div className="wp-frosted__settings-section-title">裝置資訊</div>
              {deviceInfoItems.map(item => (
                <div key={item.label} className="wp-frosted__settings-row">
                  <span className="wp-frosted__settings-label">{item.label}</span>
                  <span className="wp-frosted__settings-value">{item.value}</span>
                  {item.action === 'copy' && (
                    <button className="wp-frosted__settings-copy" onClick={() => copyToClipboard(item.value, item.label)} title="Copy">
                      📋{copiedField === item.label && <span className="wp-frosted__copied-toast">Copied</span>}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
